/**
 * Custom GPT Create Page
 * Page for creating a new Custom GPT
 */

import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Textarea } from '../../components/ui/textarea'
import { Label } from '../../components/ui/label'
import { Switch } from '../../components/ui/switch'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { CustomGPTNavbar } from '../../components/custom-gpt/CustomGPTNavbar'
import { KnowledgeBaseUpload } from '../../components/custom-gpt/KnowledgeBaseUpload'
import { createCustomGPT, uploadKnowledgeBaseFile } from '../../lib/api/customGPT'
import { toast } from 'sonner'
import { ArrowLeft, Plus, X, Upload } from 'lucide-react'
import type { CustomGPTFile } from '../../types/custom-gpt'
import { isValidFileType, isValidFileSize } from '../../lib/fileUtils'

export function CustomGPTCreatePage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    instruction: '',
    is_public: false,
  })
  const [conversationStarters, setConversationStarters] = useState<string[]>([''])
  const [filesToUpload, setFilesToUpload] = useState<File[]>([])
  const [createdGptId, setCreatedGptId] = useState<string | null>(null)
  const [uploadedFiles, setUploadedFiles] = useState<CustomGPTFile[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validation
    if (!formData.name.trim()) {
      toast.error('Name is required')
      return
    }
    if (!formData.instruction.trim()) {
      toast.error('Instruction is required')
      return
    }

    setLoading(true)
    try {
      const filteredStarters = conversationStarters.filter(s => s.trim())
      const result = await createCustomGPT({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        instruction: formData.instruction.trim(),
        is_public: formData.is_public,
        conversation_starters: filteredStarters.length > 0 ? filteredStarters : undefined,
      })
      
      setCreatedGptId(result.id)

      // Upload files if any were selected
      if (filesToUpload.length > 0) {
        const uploadPromises = filesToUpload.map(async (file) => {
          try {
            const uploadedFile = await uploadKnowledgeBaseFile(result.id, file)
            return {
              id: uploadedFile.id,
              file_name: uploadedFile.file_name,
              file_size: uploadedFile.file_size,
              file_type: uploadedFile.file_type,
              uploaded_at: new Date().toISOString(),
            } as CustomGPTFile
          } catch (error) {
            toast.error(`Failed to upload ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`)
            return null
          }
        })

        const uploaded = await Promise.all(uploadPromises)
        const successful = uploaded.filter((f): f is CustomGPTFile => f !== null)
        setUploadedFiles(successful)
        
        if (successful.length > 0) {
          toast.success(`Playbook created and ${successful.length} file(s) uploaded successfully!`)
        } else {
          toast.success('Playbook created successfully!')
        }
      } else {
        toast.success('Playbook created successfully!')
      }
      
      navigate('/playbooks')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create playbook')
    } finally {
      setLoading(false)
    }
  }

  const handleFilesSelected = (selectedFiles: FileList | File[]) => {
    const fileArray = Array.from(selectedFiles)
    const validFiles: File[] = []
    const errors: string[] = []

    // Validate files
    for (const file of fileArray) {
      if (!isValidFileType(file)) {
        errors.push(`${file.name}: Invalid file type. Allowed: PDF, DOCX, Excel (.xlsx, .xls), TXT`)
        continue
      }

      if (!isValidFileSize(file)) {
        errors.push(`${file.name}: File too large. Max size: 30MB`)
        continue
      }

      validFiles.push(file)
    }

    // Show errors
    if (errors.length > 0) {
      errors.forEach((error) => toast.error(error))
    }

    if (validFiles.length > 0) {
      setFilesToUpload((prev) => [...prev, ...validFiles])
      toast.success(`Added ${validFiles.length} file(s) to upload queue`)
    }
  }

  const addConversationStarter = () => {
    if (conversationStarters.length < 10) {
      setConversationStarters([...conversationStarters, ''])
    }
  }

  const removeConversationStarter = (index: number) => {
    setConversationStarters(conversationStarters.filter((_, i) => i !== index))
  }

  const updateConversationStarter = (index: number, value: string) => {
    if (value.length <= 200) {
      const updated = [...conversationStarters]
      updated[index] = value
      setConversationStarters(updated)
    }
  }

  return (
    <>
      <CustomGPTNavbar />
      <div className="min-h-screen bg-light-surface dark:bg-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/playbooks')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Playbooks
          </Button>

        <Card>
          <CardHeader>
            <CardTitle>Create Playbook</CardTitle>
            <CardDescription>
              Create a playbook with specific instructions and conversation starters
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="My Playbook"
                  maxLength={100}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  {formData.name.length}/100 characters
                </p>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="A brief description of your playbook"
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground">
                  {formData.description.length}/500 characters
                </p>
              </div>

              {/* Instruction */}
              <div className="space-y-2">
                <Label htmlFor="instruction">Instruction *</Label>
                <Textarea
                  id="instruction"
                  value={formData.instruction}
                  onChange={(e) => setFormData({ ...formData, instruction: e.target.value })}
                  placeholder="You are a helpful assistant that..."
                  rows={10}
                  maxLength={15000}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  {formData.instruction.length}/15000 characters
                </p>
              </div>

              {/* Public/Private */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="is_public">Make this playbook public</Label>
                  <p className="text-xs text-muted-foreground">
                    Other users will be able to see and use this playbook
                  </p>
                </div>
                <Switch
                  id="is_public"
                  checked={formData.is_public}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_public: checked })}
                />
              </div>

              {/* Conversation Starters */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Conversation Starters</Label>
                  {conversationStarters.length < 10 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addConversationStarter}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Starter
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Add up to 10 conversation starters (max 200 characters each)
                </p>
                <div className="space-y-2">
                  {conversationStarters.map((starter, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={starter}
                        onChange={(e) => updateConversationStarter(index, e.target.value)}
                        placeholder={`Conversation starter ${index + 1}`}
                        maxLength={200}
                      />
                      {conversationStarters.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeConversationStarter(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Knowledge Base Files */}
              <div className="space-y-2">
                {createdGptId ? (
                  <KnowledgeBaseUpload
                    gptId={createdGptId}
                    existingFiles={uploadedFiles}
                    disabled={loading}
                    onFilesChange={(files) => setUploadedFiles(files)}
                  />
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <Label>Knowledge Base Files</Label>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Upload files (PDF, DOCX, Excel, TXT) to provide context for your playbook. Max 30MB per file.
                    </p>
                    <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept=".pdf,.docx,.xlsx,.xls,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/plain"
                        onChange={(e) => {
                          if (e.target.files && e.target.files.length > 0) {
                            handleFilesSelected(e.target.files)
                            e.target.value = ''
                          }
                        }}
                        disabled={loading}
                        className="hidden"
                      />
                      <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm font-medium mb-1">
                        Click to select files or add them later
                      </p>
                      <p className="text-xs text-muted-foreground mb-3">
                        Files will be uploaded after the playbook is created. PDF, DOCX, Excel, TXT up to 30MB.
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={loading}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Select Files
                      </Button>
                    </div>
                    {filesToUpload.length > 0 && (
                      <div className="border rounded-lg p-4 bg-muted/50">
                        <p className="text-sm font-medium mb-2">
                          Files to upload after creation ({filesToUpload.length}):
                        </p>
                        <div className="space-y-1">
                          {filesToUpload.map((file, index) => (
                            <div key={index} className="flex items-center justify-between text-sm p-2 rounded bg-background">
                              <span className="truncate flex-1">{file.name}</span>
                              <span className="text-xs text-muted-foreground mr-2">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setFilesToUpload((prev) => prev.filter((_, i) => i !== index))}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-4 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/playbooks')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Playbook'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        </div>
      </div>
    </>
  )
}

